import React from 'react';
import {
  Alert,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { text } from '../../theme/colors';

const isBlank = (v) => v === undefined || v === null || v === '';

/**
 * Every required field in the template must carry an answer before the verifier
 * can move on. Mirrors the server-side validateListingFindings gate so the
 * verifier isn't bounced by a 400 after a long form.
 */
/** Keep only the questions for this listing's tenure (untagged questions apply to both). */
export const forTenure = (fields = [], tenureType) => fields.filter((f) => !f.tenure || f.tenure === (tenureType || 'title_deed'));

export const isListingFindingsComplete = (fields = [], value = {}) =>
  fields.filter((f) => f.required).every((f) => !isBlank(value[f.id]));

const pretty = (s) => String(s).replace(/_/g, ' ');

export default function ListingFindingsStep({ fields = [], value = {}, onChange, showErrors = false, listingType }) {
  // `houseOnly` fields (e.g. bedrooms/bathrooms counted on site) only make
  // sense for houses — a stand has neither, so hide them rather than show a
  // field the verifier can never usefully answer.
  const visibleFields = fields.filter((f) => !f.houseOnly || listingType === 'house');
  const set = (id, v) => onChange({ ...value, [id]: v });
  const isMissing = (f) => f.required && isBlank(value[f.id]);
  const missing = visibleFields.filter(isMissing).length;
  // Only flag individual fields once the verifier has actually tried to move
  // on — a form that is red before it is touched reads as broken.
  const flag = (f) => showErrors && isMissing(f);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>Listing findings</Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2 }}>
        Record exactly what the Deeds Office search (or, for a cession, the council or developer register), the local authority and the site showed.
        Eqaya compares these answers with the realtor&apos;s dossier.
      </Typography>
      {missing > 0 && (
        <Alert severity={showErrors ? 'error' : 'info'} role="status" aria-live="polite" sx={{ mb: 2 }}>
          {missing} required answer{missing === 1 ? '' : 's'} still missing
        </Alert>
      )}
      <Grid container spacing={2}>
        {visibleFields.map((f) => (
          <Grid item xs={12} sm={f.type === 'bool' ? 12 : 6} key={f.id}>
            {f.type === 'bool' ? (
              <FormControl required={f.required} error={flag(f)} component="fieldset">
                <FormLabel component="legend">{f.label}</FormLabel>
                <RadioGroup
                  row
                  name={f.id}
                  aria-required={f.required ? 'true' : undefined}
                  value={value[f.id] === true ? 'yes' : value[f.id] === false ? 'no' : ''}
                  onChange={(e) => set(f.id, e.target.value === 'yes')}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
                {flag(f) && <FormHelperText>Required</FormHelperText>}
              </FormControl>
            ) : f.type === 'select' ? (
              <TextField
                select
                fullWidth
                size="small"
                required={f.required}
                error={flag(f)}
                helperText={flag(f) ? 'Required' : undefined}
                label={f.label}
                value={value[f.id] ?? ''}
                onChange={(e) => set(f.id, e.target.value)}
              >
                {(f.options || []).map((o) => (
                  <MenuItem key={o} value={o}>{pretty(o)}</MenuItem>
                ))}
              </TextField>
            ) : (
              <TextField
                fullWidth
                size="small"
                required={f.required}
                error={flag(f)}
                helperText={flag(f) ? 'Required' : undefined}
                label={f.label}
                type={f.type === 'date' ? 'date' : f.type === 'number' ? 'number' : 'text'}
                InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
                inputProps={f.type === 'number' ? { min: 0 } : (f.type === 'text' ? { maxLength: 2000 } : undefined)}
                multiline={f.type === 'text' && /note|servitude|comparable/.test(f.id)}
                value={value[f.id] ?? ''}
                onChange={(e) => set(
                  f.id,
                  f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value
                )}
              />
            )}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
